import { useEffect } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useChatSocket } from "../context/ChatSocketContext";
import { chatKeys } from "./queries/useChatMessages";
import type { ChatMessage, ReactionSummary } from "../types/domain";
import { playerToString } from "../util/StringUtils";
import { getLoggedUser } from "../util/SecurityUtils";

type ChatPages = InfiniteData<ChatMessage[]> | undefined;

interface ChatEvent {
  type: "MESSAGE_CREATED" | "MESSAGE_UPDATED" | "MESSAGE_DELETED" | "MESSAGE_PINNED" | "REACTIONS_UPDATED";
  message?: ChatMessage;
  id?: number;
  pinned?: boolean;
  messageId?: number;
  reactions?: ReactionSummary[];
}

const enrichMessage = (msg: ChatMessage): ChatMessage => ({
  ...msg,
  authorName: msg.authorName ?? playerToString(msg.author, false, true),
  reactions: recomputeReactedByUser(msg.reactions ?? []),
});

const recomputeReactedByUser = (reactions: ReactionSummary[]): ReactionSummary[] => {
  const uid = getLoggedUser()?.id ?? -1;
  return reactions.map(r => ({ ...r, reactedByUser: (r.userIds ?? []).includes(uid) }));
};

const chatTopic = (tournamentId?: number | null) =>
  tournamentId != null ? `/topic/chat/tournament/${tournamentId}` : "/topic/chat/global";

export const useChatRealtime = (tournamentId?: number | null) => {
  const queryClient = useQueryClient();
  const { subscribe, isConnected } = useChatSocket();

  // On every (re)connect, refetch everything scoped to this chat so we catch up on
  // anything the socket may have missed while it was down.
  useEffect(() => {
    if (!isConnected) return;
    queryClient.invalidateQueries({ queryKey: chatKeys.scope(tournamentId) });
  }, [isConnected, queryClient, tournamentId]);

  useEffect(() => {
    const scopeKey = chatKeys.scope(tournamentId);
    const unsubscribe = subscribe(chatTopic(tournamentId), (raw) => {
      const event = raw as ChatEvent;
      if (event.type === "MESSAGE_CREATED" && event.message) {
        const msg = enrichMessage(event.message);
        queryClient.setQueriesData<ChatPages>({ queryKey: scopeKey }, (old) => {
          if (!old?.pages || old.pages.length === 0) return old;
          if (old.pages[0].some((m) => m.id === msg.id)) return old;
          const [first, ...rest] = old.pages;
          return { ...old, pages: [[msg, ...first], ...rest] };
        });
        queryClient.invalidateQueries({ queryKey: chatKeys.latestTimestamp(tournamentId) });
        return;
      }
      if (event.type === "MESSAGE_UPDATED" && event.message) {
        const msg = enrichMessage(event.message);
        queryClient.setQueriesData<ChatPages>({ queryKey: scopeKey }, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.map((m) => (m.id === msg.id ? msg : m))),
          };
        });
        // Also patch pinned list if it references this message.
        queryClient.setQueryData<ChatMessage[]>(chatKeys.pinned(tournamentId), (old) =>
          old ? old.map((m) => (m.id === msg.id ? msg : m)) : old,
        );
        return;
      }
      if (event.type === "MESSAGE_DELETED" && event.id != null) {
        const id = event.id;
        queryClient.setQueriesData<ChatPages>({ queryKey: scopeKey }, (old) => {
          if (!old?.pages) return old;
          return { ...old, pages: old.pages.map((page) => page.filter((m) => m.id !== id)) };
        });
        queryClient.setQueryData<ChatMessage[]>(chatKeys.pinned(tournamentId), (old) =>
          old ? old.filter((m) => m.id !== id) : old,
        );
        return;
      }
      if (event.type === "MESSAGE_PINNED" && event.id != null && typeof event.pinned === "boolean") {
        const id = event.id;
        const pinned = event.pinned;
        queryClient.setQueriesData<ChatPages>({ queryKey: scopeKey }, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.map((m) => (m.id === id ? { ...m, pinned } : m))),
          };
        });
        // Simpler than surgical update — just refetch pinned list.
        queryClient.invalidateQueries({ queryKey: chatKeys.pinned(tournamentId) });
        return;
      }
      if (event.type === "REACTIONS_UPDATED" && event.messageId != null && event.reactions) {
        const messageId = event.messageId;
        const nextReactions = recomputeReactedByUser(event.reactions);
        queryClient.setQueriesData<ChatPages>({ queryKey: scopeKey }, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => page.map((m) => (m.id === messageId ? { ...m, reactions: nextReactions } : m))),
          };
        });
      }
    });
    return unsubscribe;
  }, [subscribe, queryClient, tournamentId]);
};
